import { useTranslation } from "react-i18next";
import { App, Flex } from "antd";
import * as yup from "yup";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useSelector } from "react-redux";

import {
  useLazyGetUsersQuery,
  usePatchDataBlockMutation,
} from "@/redux/service";
import BaseModal from "@/components_v2/Modal/BaseModal";
import BaseButton from "@/components_v2/Button/BaseButton";
import { Roles } from "@/shared/utils/constants";
import {
  AddContributorModalProps,
  AddContributorsFormValues,
} from "../../types/types";
import FormFieldWrapper from "@/components_v2/Wrapper/FormFieldWrapper";
import { HookFormInfiniteScrollDropdown } from "@/components_v2/FormFields/ReactHookForm/fields";
import { BaseUserResponse, UserQueryParams } from "@/redux/users/types";
import { selectCurrentUser } from "@/redux/authentication/selectors";
import {
  BUTTON_SIZES,
  BUTTON_VARIANTS,
} from "@/components_v2/Button/constants";

const AddContributorsModal = ({
  open,
  setOpen,
  dataBlock,
  currentContributorIds,
}: AddContributorModalProps) => {
  const { t } = useTranslation();
  const { notification } = App.useApp();

  const [getUsers] = useLazyGetUsersQuery();
  const [patchDataBlock] = usePatchDataBlockMutation();
  const currentUser = useSelector(selectCurrentUser);

  const contributorsSchema = yup.object().shape({
    contributors: yup
      .array()
      .of(
        yup
          .number()
          .typeError(t("datablocks.errors.datablockTypeNotValid"))
          .required(t("datablocks.errors.required")),
      )
      .required(t("datablocks.errors.required"))
      .typeError(t("datablocks.errors.datablockTypeNotValid")),
  });

  const form = useForm<AddContributorsFormValues>({
    resolver: yupResolver(contributorsSchema),
    mode: "onChange",
    defaultValues: {
      contributors: [],
    },
  });

  const dropdownKey = `contributors-${open}-${currentContributorIds.join(",")}`;

  const addContributors = (value: AddContributorsFormValues) => {
    const data = {
      fieldValues: [
        {
          field: dataBlock.fieldsMapping.contributors,
          value: [...currentContributorIds, ...value.contributors],
        },
      ],
    };

    patchDataBlock({ id: dataBlock.id, data })
      .unwrap()
      .then(() => {
        setOpen(false);
        form.reset();
        notification.success({
          message: t("datablocks.labels.contributorsAdded"),
        });
      });
  };

  return (
    currentUser && (
      <BaseModal
        title={t("datablocks.labels.addContributors")}
        open={open}
        onCancel={() => setOpen(false)}
        footer={
          <Flex wrap justify="space-between" align="center" gap={"small"}>
            <BaseButton
              variant={BUTTON_VARIANTS.TERTIARY}
              size={BUTTON_SIZES.SMALL}
              onClick={() => setOpen(false)}
            >
              {t("buttons.cancel")}
            </BaseButton>

            <BaseButton
              variant={BUTTON_VARIANTS.PRIMARY}
              size={BUTTON_SIZES.SMALL}
              onClick={form.handleSubmit(addContributors)}
            >
              {t("buttons.addContributors")}
            </BaseButton>
          </Flex>
        }
      >
        <FormFieldWrapper label={t("datablocks.labels.contributors")}>
          <HookFormInfiniteScrollDropdown<BaseUserResponse, UserQueryParams>
            key={dropdownKey}
            name="contributors"
            control={form.control}
            fetchFn={getUsers}
            multiple={true}
            labelFieldName="fullName"
            valueFieldName="id"
            searchFieldName="search"
            defaultFilters={
              currentUser.role === Roles.ADMIN.value
                ? {}
                : {
                    organizations: currentUser.organization?.id,
                    exclude: currentContributorIds,
                  }
            }
          />
        </FormFieldWrapper>
      </BaseModal>
    )
  );
};

export default AddContributorsModal;
